/**
 * M5T Chat Response Web Component
 * Assistant/system message bubble (left-aligned)
 *
 * Shared component - can be used across multiple client apps
 *
 * Attributes:
 * - message: string - The message text to display (supports Markdown)
 * - timestamp: string - Timestamp to display (optional, defaults to current time)
 * - show-timestamp: boolean - Whether to show timestamp (default: true)
 * - avatar: string - URL to avatar image (optional)
 * - show-avatar: boolean - Whether to show avatar (default: false)
 * - phase: string - 'end' | 'error' from the turn envelope; styles the error state
 *
 * Usage:
 *   <m5t-chat-response message="I can help with that!" timestamp="10:32 AM"></m5t-chat-response>
 *
 * MARKDOWN RENDERER
 *
 * The renderer is injected rather than imported. `shared/` sits outside the pnpm
 * workspaces, so a bare `import 'markdown-it'` does not resolve from here - which
 * is why this component used to pull markdown-it from a CDN at runtime. The app
 * owns the dependency and hands it over:
 *
 *   import { setMarkdownLoader } from '../../../../shared/wc/m5t-chat-response.js';
 *   setMarkdownLoader(async () => {
 *     const { default: MarkdownIt } = await import('markdown-it');
 *     const md = new MarkdownIt({ html: false, breaks: true, linkify: true, typographer: true });
 *     return (text) => md.render(text);
 *   });
 *
 * With no renderer registered the bubble still displays - as escaped plain text
 * with line breaks. It never renders blank and never reaches the network.
 *
 * STREAMING
 *
 * A streamed turn rewrites `message` on every delta. Updates are coalesced to one
 * paint per animation frame, and only the content node is rewritten - the style
 * block and structure are built once. An out-of-order parse cannot land, because
 * a paint that resumes after a newer one started discards its result.
 */

/** @type {((text: string) => string) | null} */
let markdownRenderer = null;

/** @type {Promise<any> | null} */
let rendererReady = null;

/**
 * Install a synchronous markdown renderer: (text) => html
 */
export function setMarkdownRenderer(fn) {
  markdownRenderer = fn;
  rendererReady = Promise.resolve(fn);
}

/**
 * Install an async loader for the renderer. Call once at app start. An already
 * installed renderer keeps serving until this one resolves, and a failure
 * degrades to the plain-text fallback rather than breaking the bubble.
 */
export function setMarkdownLoader(load) {
  rendererReady = Promise.resolve()
    .then(load)
    .then((fn) => {
      markdownRenderer = fn;
      return fn;
    })
    .catch((err) => {
      console.warn('[M5TChatResponse] markdown renderer failed to load - falling back to plain text', err);
      return null;
    });
}

/** Escape without touching the DOM, so this works before the element is connected. */
export function escapeHtml(text) {
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Markdown if a renderer is available, escaped plain text otherwise.
 */
export async function toHtml(text) {
  if (!markdownRenderer && rendererReady) await rendererReady;

  if (markdownRenderer) {
    try {
      return markdownRenderer(text);
    } catch (err) {
      console.warn('[M5TChatResponse] markdown render failed - falling back to plain text', err);
    }
  }

  return escapeHtml(text).replace(/\n/g, '<br>');
}

const schedule = (fn) =>
  (globalThis.requestAnimationFrame || ((cb) => setTimeout(cb, 16)))(fn);

const STYLES = `
  :host {
    display: flex;
    flex-direction: column;
    align-self: flex-start;
    max-width: 70%;
    animation: slideIn 0.3s ease-out;
  }

  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .bubble-wrapper {
    display: flex;
    gap: 0.5rem;
    align-items: flex-end;
  }

  .avatar {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    object-fit: cover;
    border: 2px solid #3a3a3a;
  }

  .bubble-content {
    background: #2d2d2d;
    color: #e4e4e4;
    border-radius: 18px 18px 18px 4px;
    border: 1px solid #3a3a3a;
    padding: 0.875rem 1.25rem;
    font-size: 0.95rem;
    line-height: 1.5;
    word-wrap: break-word;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  }

  /* Failed turn - phase comes from the conversation envelope */
  :host([phase="error"]) .bubble-content {
    border-color: #DD4124;
    background: #2d2020;
  }

  /* Markdown styling */
  .bubble-content h1,
  .bubble-content h2,
  .bubble-content h3 {
    margin: 0.5em 0 0.25em 0;
    color: #edba08;
  }

  .bubble-content h1 { font-size: 1.4em; }
  .bubble-content h2 { font-size: 1.2em; }
  .bubble-content h3 { font-size: 1.1em; }

  .bubble-content p {
    margin: 0.5em 0;
  }

  .bubble-content p:first-child {
    margin-top: 0;
  }

  .bubble-content p:last-child {
    margin-bottom: 0;
  }

  .bubble-content code {
    background: #1a1a1a;
    padding: 0.2em 0.4em;
    border-radius: 3px;
    font-family: 'Courier New', monospace;
    font-size: 0.9em;
    color: #92a8d1;
  }

  .bubble-content pre {
    background: #1a1a1a;
    padding: 1em;
    border-radius: 6px;
    overflow-x: auto;
    margin: 0.5em 0;
  }

  .bubble-content pre code {
    background: none;
    padding: 0;
    color: #e4e4e4;
  }

  .bubble-content ul,
  .bubble-content ol {
    margin: 0.5em 0;
    padding-left: 1.5em;
  }

  .bubble-content li {
    margin: 0.25em 0;
  }

  .bubble-content a {
    color: #92a8d1;
    text-decoration: underline;
  }

  .bubble-content a:hover {
    color: #edba08;
  }

  .bubble-content blockquote {
    border-left: 3px solid #3a3a3a;
    padding-left: 1em;
    margin: 0.5em 0;
    color: #a8a8a8;
  }

  .bubble-timestamp {
    font-size: 0.75rem;
    color: #a8a8a8;
    margin-top: 0.25rem;
    padding: 0 0.5rem;
    text-align: left;
  }

  .bubble-timestamp[hidden] {
    display: none;
  }

  /* Responsive Design */
  @media (max-width: 768px) {
    :host {
      max-width: 85%;
    }

    .bubble-content {
      padding: 0.75rem 1rem;
      font-size: 0.9rem;
    }

    .avatar {
      width: 28px;
      height: 28px;
    }
  }
`;

class M5TChatResponse extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this._built = false;
    this._pending = false;
    this._painted = null;
  }

  static get observedAttributes() {
    return ['message', 'timestamp', 'show-timestamp', 'avatar', 'show-avatar'];
  }

  connectedCallback() {
    this.buildShell();
    this.paint();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue || !this._built) return;

    if (name === 'message') {
      this.schedulePaint();
      return;
    }

    if (name === 'avatar' || name === 'show-avatar') {
      // Structural - cheap to rebuild, and it never happens mid-stream.
      this.buildShell();
      this._painted = null;
      this.paint();
      return;
    }

    this.paintTimestamp();
  }

  getMessage() {
    return this.getAttribute('message') || '';
  }

  getTimestamp() {
    return this.getAttribute('timestamp') ||
      new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  }

  shouldShowTimestamp() {
    return this.getAttribute('show-timestamp') !== 'false';
  }

  getAvatar() {
    return this.getAttribute('avatar') || '';
  }

  shouldShowAvatar() {
    return this.getAttribute('show-avatar') === 'true' && !!this.getAvatar();
  }

  /**
   * Build the style block and structure once. Streaming updates touch only the
   * content node, so the stylesheet is never re-parsed mid-answer.
   */
  buildShell() {
    const avatar = this.shouldShowAvatar()
      ? `<img src="${escapeHtml(this.getAvatar())}" alt="Avatar" class="avatar">`
      : '';

    this.shadowRoot.innerHTML = `
      <style>${STYLES}</style>
      <div class="bubble-wrapper">
        ${avatar}
        <div>
          <div class="bubble-content"></div>
          <div class="bubble-timestamp"></div>
        </div>
      </div>
    `;

    this._content = this.shadowRoot.querySelector('.bubble-content');
    this._timestamp = this.shadowRoot.querySelector('.bubble-timestamp');
    this._built = true;

    this.paintTimestamp();
  }

  paintTimestamp() {
    if (!this._timestamp) return;
    this._timestamp.textContent = this.getTimestamp();
    this._timestamp.hidden = !this.shouldShowTimestamp();
  }

  /** Coalesce a burst of deltas into one paint per frame. */
  schedulePaint() {
    if (this._pending) return;
    this._pending = true;

    schedule(() => {
      this._pending = false;
      this.paint();
    });
  }

  async paint() {
    if (!this._built || !this._content) return;

    const text = this.getMessage();
    if (text === this._painted) return;

    // Connected but not yet given a message - the canvas sets `message` right
    // after prepending. Skip the empty parse rather than paint nothing twice.
    if (text === '' && this._painted === null) return;

    // Claim this text before awaiting: a paint that resumes after a newer one
    // started must discard its (now stale) output.
    this._painted = text;

    const html = await toHtml(text);
    if (this._painted !== text) return;

    this._content.innerHTML = html;
  }
}

// Define the custom element
customElements.define('m5t-chat-response', M5TChatResponse);

export default M5TChatResponse;
